import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ClientResponseError } from 'pocketbase';
import { adminRoutes, publicRoutes } from '@config/routes';
import { useAuth } from '@shared/hooks/useAuth';
import { useAuthStore, type User } from '@state/auth.store';
import './LoginSuperAdminPage.scss';

const loginSchema = z.object({
  identity: z.string().min(1, 'Vui lòng nhập email hoặc tên đăng nhập'),
  password: z.string().min(1, 'Vui lòng nhập mật khẩu'),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginSuperAdminPage() {
  const navigate = useNavigate();
  const { loginSuperAdmin, isAuthenticated, user } = useAuth();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identity: '',
      password: '',
      rememberMe: false,
    },
  });

  useEffect(() => {
    if (isAuthenticated && user && user.isSuperAdmin) {
      navigate(adminRoutes.dashboard, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const onSubmit = async (values: LoginFormValues) => {
    setIsSubmitting(true);

    try {
      const authData = await loginSuperAdmin(values.identity, values.password);

      const userRecord = authData.record as User;
      const updatedUser: User = {
        ...userRecord,
        rememberMe: values.rememberMe,
      };

      useAuthStore.getState().setUser(updatedUser);

      localStorage.setItem('hrm-remember-me', JSON.stringify(values.rememberMe));

      toast.success('Đăng nhập thành công');
      navigate(adminRoutes.dashboard, { replace: true });
    } catch (error) {
      let message = 'Đăng nhập thất bại. Vui lòng thử lại.';

      if (error instanceof ClientResponseError) {
        if (error.status === 400) {
          message = 'Thông tin đăng nhập không chính xác.';
        } else if (error.status >= 500) {
          message = 'Máy chủ gặp sự cố, vui lòng thử lại sau.';
        }
      }

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="super-admin-auth-page">
      <div className="super-admin-auth-container">
        <div className="super-admin-auth-wrapper">
          <div className="super-admin-auth-inner">
            <div className="super-admin-auth-card">
              <div className="super-admin-auth-card-body">
                {/* Logo */}
                <div className="super-admin-auth-brand">
                  <Link to={publicRoutes.home} className="super-admin-auth-brand-link">
                    <span className="super-admin-auth-brand-logo">
                      <svg width="32" height="22" viewBox="0 0 32 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0.00172773 0V6.85398C0.00172773 6.85398 -0.133178 9.01207 1.98092 10.8388L13.6912 21.9964L19.7809 21.9181L18.8042 9.88248L16.4951 7.17289L9.23799 0H0.00172773Z"
                          fill="#7367F0"
                        />
                        <path
                          opacity="0.06"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M7.69824 16.4364L12.5199 3.23696L16.5541 7.25596L7.69824 16.4364Z"
                          fill="#161616"
                        />
                        <path
                          opacity="0.06"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.07751 15.9175L13.9419 4.63989L16.5849 7.28475L8.07751 15.9175Z"
                          fill="#161616"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M7.77295 16.3566L23.6563 0H32V6.88383C32 6.88383 31.8262 9.17836 30.6591 10.4057L19.7824 22H13.6938L7.77295 16.3566Z"
                          fill="#7367F0"
                        />
                      </svg>
                    </span>
                    <span className="super-admin-auth-brand-text">HRM SaaS</span>
                  </Link>
                </div>
                {/* /Logo */}
                <h4 className="super-admin-auth-title">Welcome to HRM SaaS Admin Panel 👋</h4>
                <p className="super-admin-auth-subtitle">Please sign-in to your admin account and start managing the system</p>

                <form className="super-admin-auth-form" onSubmit={handleSubmit(onSubmit)}>
                  <div className="super-admin-auth-form-group">
                    <label htmlFor="super-admin-identity" className="super-admin-auth-form-label">
                      Email or Username
                    </label>
                    <input
                      type="text"
                      className={`super-admin-auth-form-control ${errors.identity ? 'super-admin-auth-is-invalid' : ''}`}
                      id="super-admin-identity"
                      placeholder="Enter your email or username"
                      autoFocus
                      {...register('identity')}
                    />
                    {errors.identity && (
                      <div className="super-admin-auth-invalid-feedback">{errors.identity.message}</div>
                    )}
                  </div>
                  <div className="super-admin-auth-form-group super-admin-auth-password-toggle">
                    <div className="super-admin-auth-password-header">
                      <label className="super-admin-auth-form-label" htmlFor="super-admin-password">
                        Password
                      </label>
                      <Link to={publicRoutes.forgotPassword} className="super-admin-auth-forgot-link">
                        <small>Forgot Password?</small>
                      </Link>
                    </div>
                    <div className="super-admin-auth-input-group">
                      <input
                        type={isPasswordVisible ? 'text' : 'password'}
                        id="super-admin-password"
                        className={`super-admin-auth-form-control ${errors.password ? 'super-admin-auth-is-invalid' : ''}`}
                        placeholder="••••••••"
                        aria-describedby="password"
                        {...register('password')}
                      />
                      <span
                        className="super-admin-auth-input-group-text"
                        role="button"
                        tabIndex={0}
                        onClick={() => setIsPasswordVisible((prev) => !prev)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setIsPasswordVisible((prev) => !prev);
                          }
                        }}
                        aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                      >
                        <i className={`ti ${isPasswordVisible ? 'ti-eye' : 'ti-eye-off'}`}></i>
                      </span>
                    </div>
                    {errors.password && (
                      <div className="super-admin-auth-invalid-feedback">{errors.password.message}</div>
                    )}
                  </div>
                  <div className="super-admin-auth-form-group">
                    <div className="super-admin-auth-form-check">
                      <input
                        className="super-admin-auth-form-check-input"
                        type="checkbox"
                        id="super-admin-remember-me"
                        {...register('rememberMe')}
                      />
                      <label className="super-admin-auth-form-check-label" htmlFor="super-admin-remember-me">
                        Remember Me
                      </label>
                    </div>
                  </div>
                  <div className="super-admin-auth-form-group">
                    <button
                      className="super-admin-auth-btn super-admin-auth-btn-primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Signing in...' : 'Sign in'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

